import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

let lexicalHeaderElements: Array<Element> = [];
let headerUpdateCallbacks: Array<() => void> = [];

interface HeaderInfo {
    element: HTMLElement;
    id: string;
    rect: DOMRect;
}

function HashButton({ header }: { header: HeaderInfo; }) {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const hideTimerRef = useRef<number | null>(null);
    const buttonRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const updatePosition = () => {
            const rect = header.element.getBoundingClientRect();
            setPosition({
                x: rect.left - 45,
                y: rect.top
            });
        };

        updatePosition();

        const handleScroll = () => updatePosition();
        const handleResize = () => updatePosition();

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [header.element]);

    useEffect(() => {
        const handleMouseEnter = () => {
            if (hideTimerRef.current) {
                window.clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
            setIsVisible(true);
        };

        const handleMouseLeave = (e: MouseEvent) => {
            const toEl = (e.relatedTarget as Node) || null;
            if (toEl && buttonRef.current && buttonRef.current.contains(toEl)) {
                return;
            }
            hideTimerRef.current = window.setTimeout(() => {
                setIsVisible(false);
            }, 150);
        };

        header.element.addEventListener('mouseenter', handleMouseEnter);
        header.element.addEventListener('mouseleave', handleMouseLeave as EventListener);

        return () => {
            header.element.removeEventListener('mouseenter', handleMouseEnter);
            header.element.removeEventListener('mouseleave', handleMouseLeave as EventListener);
            if (hideTimerRef.current) {
                window.clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
        };
    }, [header.element]);

    const handleClick = () => {
        if (header.id) {
            window.location.hash = '#' + header.id;
        }
    };

    if (!isVisible) return null;

    return createPortal(
        <div
            ref={buttonRef}
            className={clsx("fixed w-9 h-9 bg-white border border-gray-200 rounded-md",
                "shadow-lg flex items-center justify-center cursor-pointer hover:bg-gray-50",
                "hover:border-gray-300 transition-colors duration-150 select-none z-[9999]")}
            style={{
                left: position.x,
                top: position.y,
            }}
            onClick={handleClick}
            onMouseEnter={() => {
                if (hideTimerRef.current) {
                    window.clearTimeout(hideTimerRef.current);
                    hideTimerRef.current = null;
                }
                setIsVisible(true);
            }}
            onMouseLeave={(e) => {
                const toEl = (e.relatedTarget as Node) || null;
                if (toEl && header.element.contains(toEl)) {
                    return;
                }
                hideTimerRef.current = window.setTimeout(() => {
                    setIsVisible(false);
                }, 150);
            }}
        >
            <span className="text-gray-600 font-medium text-sm">#</span>
        </div>,
        document.body
    );
}

export function HashHighlightPlugin(): JSX.Element {
    const [editor] = useLexicalComposerContext();
    const [headers, setHeaders] = useState<HeaderInfo[]>([]);

    useEffect(() => {
        const editorElement = editor.getRootElement();
        if (!editorElement) return;

        const scanHeaders = () => {
            const allHeaders = Array.from(editorElement.querySelectorAll('h1, h2, h3, h4, h5, h6')) as HTMLElement[];
            const hashedHeaders = allHeaders.filter(h => h.classList.contains('hashed-header'));

            const headerInfos: HeaderInfo[] = hashedHeaders.map(element => ({
                element,
                id: element.id || '',
                rect: element.getBoundingClientRect()
            }));

            setHeaders(headerInfos);

            lexicalHeaderElements = hashedHeaders;
            headerUpdateCallbacks.forEach(callback => callback());
        };

        setTimeout(scanHeaders, 100);

        const observer = new MutationObserver(() => {
            setTimeout(scanHeaders, 50);
        });

        observer.observe(editorElement, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'id']
        });

        const unregister = editor.registerUpdateListener(() => {
            setTimeout(scanHeaders, 100);
        });

        return () => {
            observer.disconnect();
            unregister();
        };
    }, [editor]);

    return (
        <>
            {headers.map((header, index) => (
                <HashButton key={`${header.id}-${index}`} header={header} />
            ))}
        </>
    );
}

export function getLexicalHeaders(): Array<Element> {
    return lexicalHeaderElements;
}

export function onLexicalHeadersUpdate(callback: () => void): () => void {
    headerUpdateCallbacks.push(callback);
    return () => {
        const index = headerUpdateCallbacks.indexOf(callback);
        if (index > -1) {
            headerUpdateCallbacks.splice(index, 1);
        }
    };
}
